import { useRef, useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, Typography, styled } from '@mui/material';
import { Table } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import DialogLoading from '../../components/feedback/DialogLoading';
import AppButton from '../../components/ui/AppButton';
import { useImportPointCompany } from '../../hooks/useCompany';
import { useDialogStore } from '../../stores/dialogStore';

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const AdminImportPointPage = () => {
  const navigate = useNavigate();
  const uploadRef = useRef(null);
  const [data, setData] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const { mutateAsync: importCompanyPoint, isPending: isImportCompanyPointPending } =
    useImportPointCompany();
  const openDialog = useDialogStore((state) => state.openDialog);

  const columns = [
    {
      title: 'Code',
      minWidth: 180,
      dataIndex: 'code',
      key: 'code',
      render: (text) => text ?? '-',
    },
    {
      title: 'ยอดการเติมขั้นต่ำ',
      minWidth: 180,
      dataIndex: 'spending_criteria',
      key: 'spending_criteria',
      render: (text, record) => text ?? record?.spending_criteria ?? '-',
    },
    {
      title: 'ยอดการเติมเดือนปัจจุบัน',
      minWidth: 180,
      dataIndex: 'current_purchase_amount',
      key: 'current_purchase_amount',
      render: (text, record) => text ?? record?.current_purchase_amount ?? '-',
    },
    {
      title: 'สถานะ',
      minWidth: 180,
      dataIndex: 'status',
      key: 'status',
      render: (text) => text ?? '-',
    },
    {
      title: 'คะแนนพิเศษ',
      minWidth: 180,
      dataIndex: 'special_point',
      key: 'special_point',
      render: (text, record) => text ?? record?.special_point ?? '-',
    },
    {
      title: 'คะแนนกิจกรรม',
      minWidth: 180,
      dataIndex: 'activity_points',
      key: 'activity_points',
      render: (text, record) => text ?? record?.activity_points ?? '-',
    },
  ];

  const getRowClassName = (record) => {
    return !record.validate ? 'error-row' : '';
  };

  const onUploadFile = async (e) => {
    const file = e.target.files[0];

    if (file) {
      const formData = new FormData();
      formData.append('import_point', file);

      try {
        const response = await importCompanyPoint({
          params: { validate: true },
          payload: formData,
        });

        if (response.Code === '20000') {
          const mappingData = response.Data.map((item) => ({
            ...item,
            _id: new Date().getTime(),
          }));
          setData(mappingData);
          setExpandedRowKeys(mappingData.filter((item) => !item?.validate).map((item) => item._id));

          const res = await importCompanyPoint({ params: { validate: false }, payload: formData });
          if (res.data.Code === '20000') {
            openDialog({
              variant: 'success',
              title: 'อัพโหลดสำเร็จ',
              message: 'อัพโหลดข้อมูลสำเร็จ',
              onCloseCallback: () => {
                navigate('/admin');
              },
            });
          }
        }
      } catch (err) {
        console.error(err);

        if (err.response.data.Code === '40000') {
          const mappingData = err.response.data.Data.map((item) => ({
            ...item,
            _id: new Date().getTime(),
          }));

          setData(mappingData);
          setExpandedRowKeys(mappingData.filter((item) => !item?.validate).map((item) => item._id));
          openDialog({
            variant: 'error',
            title: 'ข้อมูลผิดพลาด',
            message: err.response.data.DeveloperMessage,
          });
        } else {
          openDialog({
            variant: 'error',
            title: 'ข้อมูลผิดพลาด',
            message: err.response.data.DeveloperMessage,
          });
        }
      } finally {
        uploadRef.current.value = null;
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>อัพโหลดข้อมูลบริษัท | Admin</title>
      </Helmet>

      <Box
        sx={{
          height: 'calc(100svh - 60px)',
          padding: '20px 20px 60px',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <Box maxWidth="xl" mx="auto">
          {/* Header */}
          <Box sx={{ position: 'relative', mb: 2 }}>
            {/* Title */}
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#002B38',
                textAlign: 'center',
              }}
            >
              อัพโหลดข้อมูลคะแนนบริษัท
            </Typography>
            {/* Back button */}
            <IconButton
              onClick={() => navigate('/admin')}
              sx={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#004B61',
                backgroundColor: 'transparent',
                position: 'absolute',
                top: '50%',
                left: 0,
                transform: 'translateY(-50%)',
                borderRadius: '8px',

                '&:hover': {
                  color: '#004B61',
                  backgroundColor: 'transparent',
                  opacity: '.85',
                },
              }}
            >
              <ArrowBackIosIcon
                sx={{
                  fontSize: '1.25rem',
                  color: '#004B61',
                }}
              />
              <Box component={'span'} sx={{ display: { xs: 'none', sm: 'inline-block' } }}>
                ย้อนกลับ
              </Box>
            </IconButton>
          </Box>

          {/* Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
              justifyContent: 'flex-end',
              alignItems: {
                xs: 'flex-start',
                sm: 'center',
              },
              mb: 2,
            }}
          >
            {/* Download and Upload button */}
            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <AppButton
                variant="danger"
                component="a"
                startIcon={<DownloadIcon />}
                href="https://s3.starcardclub.com/starcardclub/import/import_point_temp.xlsx"
                download
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: {
                    xs: 1,
                    sm: 2,
                  },
                }}
              >
                Template File
              </AppButton>
              <AppButton
                variant="brand"
                component="label"
                startIcon={<CloudUploadIcon />}
                href="#file-upload"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: {
                    xs: 1,
                    sm: 2,
                  },
                }}
              >
                Import XLSX
                <VisuallyHiddenInput
                  ref={uploadRef}
                  type="file"
                  id="company_file"
                  name="company_file"
                  onChange={onUploadFile}
                />
              </AppButton>
            </Box>
          </Box>

          {/* Table */}
          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              overflow: 'hidden',

              '.ant-table-thead > tr': {
                background: 'linear-gradient(90deg,#002B38,#002B38,#002B38,#005D76,#006B7D)',
              },

              '.ant-table-row-expand-icon-cell > svg': {
                transform: 'translateY(5px)',
              },

              '.ant-table-expanded-row': {
                backgroundColor: '#ffebee',
              },

              '.error-row': {
                backgroundColor: '#ffebee !important',
              },
            }}
          >
            <Table
              columns={columns}
              dataSource={data}
              loading={isImportCompanyPointPending}
              rowKey={(record) => record._id}
              rowClassName={getRowClassName}
              rowHoverable={false}
              expandable={{
                expandedRowRender: (record) => (
                  <Typography color="error" sx={{ fontSize: '18px' }}>
                    Error Reason: {record?.validate_reason || '-'}
                  </Typography>
                ),
                expandedRowKeys: expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
                expandIcon: ({ expanded, onExpand, record }) =>
                  record.validate ? null : !expanded ? (
                    <ExpandMoreIcon
                      sx={{
                        color: '#002B38',
                        fontSize: '24px',
                      }}
                      onClick={(e) => onExpand(record, e)}
                    />
                  ) : (
                    <ExpandLessIcon
                      sx={{
                        color: '#002B38',
                        fontSize: '24px',
                      }}
                      onClick={(e) => onExpand(record, e)}
                    />
                  ),
              }}
              pagination={{
                pageSize: 50,
                showSizeChanger: false,
              }}
              scroll={{
                x: 'max-content',
              }}
            />
          </Box>
        </Box>
      </Box>
      <DialogLoading open={isImportCompanyPointPending} message="กำลังอัพโหลดไฟล์" />
    </>
  );
};

export default AdminImportPointPage;
